import * as React from 'react';

import { compose } from 'recompose';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import AutoenrolledCoursesModal from 'bundles/enterprise-legacy-learner-home/components/modals/AutoenrolledCoursesModal';
import ForcedSSORecoveryEmailModal from 'bundles/enterprise-legacy-learner-home/components/modals/ForcedSSORecoveryEmailModal';
import ProgramAccessModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramAccessModal';
import ProgramLearnerApiManager from 'bundles/enterprise-legacy-learner-home/utils/ProgramLearnerApiManager';
import type { InjectedNaptime } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCourses } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useIsGwGProgram,
  useProgram,
  useProgramMembership,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import type { ProgramJoinErrorModalInfo } from 'bundles/unified-common/types/enterprise';
import ProgramAtCapacityModal from 'bundles/unified-description-page-common/components/shared/modals/ProgramAtCapacityModal';

const EnterpriseTermsOfUseModal = createLoadableComponent(
  () => import('bundles/unified-common/components/enterprise/EnterpriseTermsOfUseModal')
);

type Props = {
  naptime: InjectedNaptime;
};

const EnterpriseModalManager = ({ naptime }: Props) => {
  const router = useRouter();
  const modalManager = useEnterpriseModalManager();
  const program = useProgram();
  const { reloadProgramMembership: refetchProgramMembership, ...programMembership } = useProgramMembership();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isGwGProgram = useIsGwGProgram();
  const autoEnrolledCourses = useEnterpriseAutoEnrolledCourses();
  const features = useEnterpriseProductFeatures();

  const setProgramJoinErrorModalState = React.useCallback<
    (programJoinErrorModalInfo: ProgramJoinErrorModalInfo) => void
  >(
    (programJoinModalErrorInfo) => {
      modalManager?.setProgramJoinErrorInfo(programJoinModalErrorInfo);
    },
    [modalManager]
  );

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  const apiManager = new ProgramLearnerApiManager({ program, naptime });
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE) {
    return (
      <EnterpriseTermsOfUseModal
        showTermsOfUseModal={modalManager.showEnterpriseTermsOfUseModal}
        setShowTermsOfUseModal={modalManager.setShowEnterpriseTermsOfUseModal}
        thirdPartyOrganization={thirdPartyOrganization}
        isEnterpriseAdmin={modalManager.isEnterpriseAdmin}
        shouldShowCCPAMarketingConsent={modalManager.shouldShowCCPAMarketingConsent}
        shouldShowGDPRMarketingConsent={modalManager.shouldShowGDPRMarketingConsent}
        shouldShowThirdPartyTermsOfConsent={modalManager.shouldShowThirdPartyTermsOfConsent}
      />
    );
  }
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS) {
    return (
      <ProgramAccessModal
        apiManager={apiManager}
        isAccessModalDismissed={modalManager?.isAccessModalDismissed}
        isProgramAdmin={!!programMembership.isProgramAdmin}
        onCloseAccessModal={() => modalManager?.setIsAccessModalDismissed?.(true)}
        openProgramJoinErrorModal={setProgramJoinErrorModalState}
        program={{
          ...program,
          name: program?.name,
          slug: program?.slug,
        }}
        programMembership={programMembership?.membership}
        reloadProgramMembership={() => {
          void refetchProgramMembership?.();
        }}
        router={router}
        thirdPartyOrganization={thirdPartyOrganization}
        userId={user.get()?.id}
        // Not used in component.
        isUpswell={false}
        // Not used in component.
        isNonBlacklistedC4cv={false}
        isGwGProgram={isGwGProgram}
        programRecommendationsAvailableCoursesCopy={!!features?.programRecommendationsAvailableCoursesCopy}
        autoEnrolledCoursesWithData={autoEnrolledCourses}
      />
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.LOGIN) {
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, authMode: 'login' },
    });

    modalManager.setHasShownLoginModal(true);

    return null;
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES && autoEnrolledCourses) {
    return (
      <AutoenrolledCoursesModal
        userId={user.get()?.id}
        autoEnrolledCoursesWithData={autoEnrolledCourses}
        setIsAutoenrolledCoursesModalDismissed={modalManager?.setIsAutoenrolledCoursesModalDismissed || (() => {})}
        programId={program.id}
        programSlug={program.slug}
      />
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ADD_EMAIL) {
    return <ForcedSSORecoveryEmailModal programId={program.id} userId={user.get()?.id} />;
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.PROGRAM_JOIN_ERROR) {
    return (
      <ProgramAtCapacityModal
        programJoinErrorCode={modalManager.programJoinErrorInfo.errorCode!}
        isOpen
        setOpen={(isOpen) => {
          if (!isOpen) {
            setProgramJoinErrorModalState({ errorCode: modalManager.programJoinErrorInfo.errorCode, showModal: false });
          }
        }}
      />
    );
  } else {
    return null;
  }
};

const EnterpriseModalManagerWithErrorBoundary = ({ naptime }: Props) => {
  return (
    <ErrorBoundaryWithTagging componentName="EnterpriseModalManager">
      <EnterpriseModalManager naptime={naptime} />
    </ErrorBoundaryWithTagging>
  );
};

export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterpriseModalManagerWithErrorBoundary);
